import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import { getFocusStyleObject } from 'foundations-components/transitional/utils/focus';
import styled from 'foundations-theming/utils';
import { useCallback, useId, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CARD_MIN_INLINE_SIZE = '135px';
const StyledCard = styled.label.withConfig({
  displayName: "AgentPackCard__StyledCard"
})(({
  theme,
  $selected,
  $focused
}) => Object.assign({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minWidth: CARD_MIN_INLINE_SIZE,
  scrollSnapAlign: 'start',
  gap: theme.space[100],
  paddingBlock: theme.space[400],
  paddingInline: theme.space[200],
  borderStyle: 'solid',
  borderWidth: theme.borderWidth[100],
  borderRadius: theme.borderRadius[400],
  cursor: 'pointer',
  position: 'relative',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  userSelect: 'none',
  backgroundColor: $selected ? theme.color['fill-surface-recessed'] : theme.color['fill-surface-default'],
  borderColor: theme.color['border-core-default']
}, $focused ? getFocusStyleObject({
  theme,
  unwrapped: true
}) : {}));
const StyledBadge = styled.span.withConfig({
  displayName: "AgentPackCard__StyledBadge"
})(({
  theme
}) => ({
  position: 'absolute',
  top: 0,
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.color['fill-accent-neutral-default'],
  paddingBlock: theme.space[25],
  paddingInline: theme.space[100],
  borderRadius: theme.borderRadius.full,
  whiteSpace: 'nowrap'
}));

/* Primary row: price for paid packs, the label ("Included"/"Other") otherwise. */
function renderPrimaryRow(pack, currencyCode) {
  if (pack.type === 'paid') {
    return /*#__PURE__*/_jsx(Text, {
      typography: "heading-50",
      whiteSpace: "nowrap",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "agentPacksComponents.pricePerMonth",
        options: {
          price: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
            value: Math.ceil(pack.pricePerMonth),
            currencyCode: currencyCode,
            trailingZeroDisplay: "stripIfInteger"
          })
        }
      })
    });
  }
  return /*#__PURE__*/_jsx(Text, {
    typography: "heading-50",
    whiteSpace: "nowrap",
    children: pack.label
  });
}

/*
 * Secondary row: the credit amount. The included pack shows its baseline with no
 * "+" (it's not additive); paid packs show "+N credits"; custom shows a prompt.
 */
function renderSecondaryRow(pack, baselineCredits) {
  switch (pack.type) {
    case 'included':
      return /*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        whiteSpace: "nowrap",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "agentPacksComponents.includedCredits",
          options: {
            credits: /*#__PURE__*/_jsx(FormattedNumber, {
              value: baselineCredits
            })
          }
        })
      });
    case 'paid':
      return /*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        whiteSpace: "nowrap",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "agentPacksComponents.credits",
          options: {
            credits: /*#__PURE__*/_jsx(FormattedNumber, {
              value: pack.credits
            })
          }
        })
      });
    case 'custom':
      return /*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        whiteSpace: "nowrap",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "agentPacksComponents.chooseAmount"
        })
      });
    default:
      return null;
  }
}
export const AgentPackCard = ({
  pack,
  isSelected,
  baselineCredits,
  currencyCode,
  groupName,
  onSelect
}) => {
  const labelId = useId();
  const [isFocused, setIsFocused] = useState(false);
  const handleFocus = useCallback(evt => {
    try {
      if (evt.target.matches(':focus-visible')) {
        setIsFocused(true);
      }
    } catch (_unused) {
      setIsFocused(true);
    }
  }, []);
  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);
  const isRecommended = pack.type === 'paid' && pack.isRecommended === true;
  return /*#__PURE__*/_jsxs(StyledCard, {
    $selected: isSelected,
    $focused: isFocused,
    children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: /*#__PURE__*/_jsx("input", {
        type: "radio",
        name: groupName,
        value: pack.id,
        checked: isSelected,
        onChange: () => onSelect(pack.id),
        onFocus: handleFocus,
        onBlur: handleBlur,
        "aria-labelledby": labelId,
        "data-test-id": `agent-packs-${pack.id}-option`
      })
    }), isRecommended && /*#__PURE__*/_jsx(StyledBadge, {
      children: /*#__PURE__*/_jsx(Text, {
        typography: "detail-200",
        color: "text-core-on-fill-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "agentPacksComponents.recommended"
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      gap: "100",
      id: labelId,
      children: [renderPrimaryRow(pack, currencyCode), renderSecondaryRow(pack, baselineCredits)]
    })]
  });
};